import React, { useEffect, useState, forwardRef } from "react";
import Portal from "../default-components/Portal";
import * as faceapi from "face-api.js";
import { loadModels } from "./modelLoader";
// import { saveFaceData } from "./models/faceStorage.module";
// import { detectFaces } from "./models/faceDetection.module";
import { recognizeFace } from "./models/faceRecognition.module";
import { timestamp } from "../firebase-config/config";
import { BeatLoader } from "react-spinners";
import "./FaceRegister.scss";

// FIREBASE
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";
import { projectStorage } from "../firebase-config/config";
import { useFirestore } from "../firebase-files/hooks/useFirestore";
import PGLoaders from "../default-pages/PGLoaders/PGLoaders";
const FaceRegister = () => {
  const { user } = useAuthContext();

  // FIREBASE
  const { updateDocument, error } = useFirestore(
    "users",
    user && user.phoneNumber
  );

  const [status, setStatus] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isCamOpenStatus, setIsCamOpenStatus] = useState(false); // 👈 Add this state
  const [faceScanned, setFaceScanned] = useState(false);
  const [faceRegistring, setFaceRegistring] = useState(false);
  const [faceRegistered, setFaceRegistered] = useState(false);
  const [isScanning, setIsScanning] = useState(false); // 👈 Add this state

  const videoRef = React.useRef(null);
  const [descriptor, setDescriptor] = useState(null);

  useEffect(() => {
    const retryInterval = setInterval(() => {
      if (!videoRef.current?.srcObject) {
        navigator.mediaDevices
          .getUserMedia({ video: true })
          .then((stream) => {
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              setIsCamOpenStatus(true);
            }
          })
          .catch(() => {
            // Don't log too often
          });
      }
    }, 3000);

    return () => clearInterval(retryInterval);
  }, []);

  useEffect(() => {
    const start = async () => {
      await loadModels(); // models load
    };
    start();
  }, []);

  const captureImage = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageUrl = canvas.toDataURL("image/png");
    setPreviewUrl(imageUrl);
  };

  const handleScan = async () => {
    if (!videoRef.current || isScanning) return;

    setIsScanning(true); // 👈 prevent multi-click
    setStatus("This may take up to 10 seconds...");

    const video = videoRef.current;

    // Optional: small delay before actually scanning, to feel more natural
    await new Promise((res) => setTimeout(res, 500));

    const detections = await faceapi
      .detectSingleFace(video)
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (detections) {
      const successSound = new Audio("/assets/sounds/success.mp3");
      successSound.play();
      const faceDescriptor = Array.from(detections.descriptor);
      setDescriptor(faceDescriptor);
      captureImage();
      setStatus("Face scanned Successfully ✅");
      setFaceScanned(true);
    } else {
      const errorSound = new Audio("/assets/sounds/error.mp3");
      errorSound.play();
      setStatus("No face detected ❌");
    }

    setIsScanning(false); // 👈 allow clicking again after done
  };

  const handleRegister = async () => {
    setFaceRegistring(true);
    if (!previewUrl) {
      const errorSound = new Audio("/assets/sounds/error.mp3");
      errorSound.play();
      setStatus("❌ No image captured");
      setFaceRegistring(false);
      return;
    }

    setStatus("Face Registring...");

    const img = new Image();
    img.src = previewUrl;

    img.onload = async () => {
      const faceDescriptor = await recognizeFace(img);

      if (!faceDescriptor) {
        const errorSound = new Audio("/assets/sounds/error.mp3");
        errorSound.play();
        setFaceRegistring(false);
        setStatus("Face not detected.");
        return;
      }

      if (!user) {
        const errorSound = new Audio("/assets/sounds/error.mp3");
        errorSound.play();
        setStatus("User data not loaded yet. Try again.");
        setFaceRegistring(false);
        return;
      }

      // const plainDescriptor = Array.from(faceDescriptor);

      try {
        // ✅ Try deleting old face image (if it exists)
        if (user?.registeredFaceUrl) {
          try {
            const oldRef = projectStorage.refFromURL(user.registeredFaceUrl);
            await oldRef.delete();
            console.log("✅ Old face image deleted");
          } catch (deleteError) {
            if (deleteError.code === "storage/object-not-found") {
              console.warn("⚠️ Old face image not found, skipping delete");
            } else {
              throw deleteError; // Rethrow if it's some other error
            }
          }
        }

        // 🔼 Upload image to Firebase Storage
        const blob = await (await fetch(previewUrl)).blob(); // convert base64 to blob
        const imageName = `face_${Date.now()}.png`;
        const uploadPath = `registered-faces-for-scan/${user.phoneNumber}/${imageName}`;
        const storageRef = projectStorage.ref(uploadPath);

        await storageRef.put(blob);
        const imageUrl = await storageRef.getDownloadURL();

        // 🔼 Prepare Firestore update
        const plainDescriptor = Array.from(faceDescriptor);
        const newEntry = {
          faceDescriptor: plainDescriptor,
          createdAt: timestamp.now(),
        };

        await updateDocument(user && user.phoneNumber, {
          faceRegistration: [newEntry],
          registeredFaceUrl: imageUrl,
        });
        const successSound = new Audio("/assets/sounds/success.mp3");
        successSound.play();
        setStatus("✅ Face registered!");
        setDescriptor(faceDescriptor);
        setFaceRegistered(true);
        setFaceRegistring(false);
      } catch (error) {
        console.error("Firestore update error:", error);
        setStatus("❌ Error saving to database");
        const errorSound = new Audio("/assets/sounds/error.mp3");
        errorSound.play();
        setFaceRegistring(false);
      }
    };

    img.onerror = () => {
      setStatus("❌ Image not loaded.");
      const errorSound = new Audio("/assets/sounds/error.mp3");
      errorSound.play();
      setFaceRegistring(false);
    };
  };

  const handlePopup = () => {
    setShowPopup(false);
    setFaceScanned(false);
    setIsScanning(false);
    setStatus("");
    setIsCamOpenStatus(false);
    setFaceRegistered(false);
  };
  return (
    <div>
      <button className="btn_fill" onClick={() => setShowPopup(true)}>
        Register Face
      </button>
      {/* START CODE FACE REGISTER POPUP */}
      {showPopup && (
        <Portal>
          <div className="face-register-overlay">
            <div className="face-register-container">
              <div className="face-register-card">
                <div className="face-register-header">
                  <span>
                    {new Date().toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span onClick={handlePopup}>
                    {!faceRegistring && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="20px"
                        viewBox="0 -960 960 960"
                        width="20px"
                        fill="currentColor"
                        style={{ cursor: "pointer" }}
                      >
                        <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                      </svg>
                    )}
                  </span>
                </div>
                <div className="face-register-title">Face Registration</div>
                <div className="face-register-content">
                  {faceRegistered ? (
                    <>
                      <div className="face-register-check-icon">✔️</div>
                      <h2 className="face-register-congrats">Congrats!</h2>
                      <p className="face-register-message">{status}</p>
                      <div className="text-center">
                        <button className="btn_fill" onClick={handlePopup}>
                          Okay
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="face-register-message">{status}</p>
                      <div className="face-register-avatar">
                        <div className={isScanning ? "video-scan-border" : ""}>
                          <video
                            ref={videoRef}
                            autoPlay
                            muted
                            style={{
                              width: "150px",
                              height: "150px",
                              borderRadius: "50%",
                              border: "2px solid #4e9bff",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      </div>
                      <div className="face-register-details">
                        {/* <div className="face-register-detail-left">
                          <p>
                            <strong>Last Registration</strong>
                          </p>
                          <p>
                            {user?.faceRegistration &&
                            user.faceRegistration.length > 0
                              ? new Date(
                                  Math.max(
                                    ...user.faceRegistration.map((entry) =>
                                      entry.createdAt?.toDate
                                        ? entry.createdAt.toDate().getTime()
                                        : 0
                                    )
                                  )
                                ).toLocaleString("en-IN", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "No registrations yet"}
                          </p>
                        </div> */}
                        {/* <div className="face-register-detail-right">
                          <p>
                            <strong>Current Date</strong>
                          </p>
                          <p>11 Apr 2025</p>
                        </div> */}
                      </div>

                      <div className="d-flex justify-content-between">
                        <button
                          className="btn_border"
                          onClick={handlePopup}
                          disabled={faceRegistring}
                        >
                          Cancel
                        </button>
                        {faceScanned ? (
                          <button className="btn_fill" onClick={handleRegister}>
                            Register
                          </button>
                        ) : isCamOpenStatus ? (
                          <button
                            className="btn_fill"
                            onClick={handleScan}
                            disabled={isScanning}
                          >
                            {isScanning ? "Scanning..." : "Scan"}
                          </button>
                        ) : (
                          <div className="d-flex align-items-center">
                            <PGLoaders />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}
      {/* END CODE FACE REGISTER POPUP */}
    </div>
  );
};

export default FaceRegister;
